import * as React from 'react';
import PropTypes from 'prop-types';

// Color presets for sensors
export const SENSOR_COLORS = [
  { value: '#22c55e', label: 'Green' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#84cc16', label: 'Lime' },
  { value: '#f97316', label: 'Orange' },
  { value: '#6366f1', label: 'Indigo' },
];

// Available sensor types with their default configurations
export const SENSOR_TYPES = {
  ph: { label: 'pH Level', unit: '', min: 0, max: 14, optimalMin: 5.5, optimalMax: 6.5, meterType: 'gauge', icon: 'WaterDrop', defaultColor: '#3b82f6' },
  ec: { label: 'EC', unit: 'mS/cm', min: 0, max: 5, optimalMin: 1.2, optimalMax: 2.0, meterType: 'level', icon: 'Bolt', defaultColor: '#f59e0b' },
  tds: { label: 'TDS', unit: 'ppm', min: 0, max: 2000, optimalMin: 600, optimalMax: 1000, meterType: 'level', icon: 'Science', defaultColor: '#8b5cf6' },
  waterTemp: { label: 'Water Temp', unit: '°F', min: 50, max: 90, optimalMin: 65, optimalMax: 72, meterType: 'thermometer', icon: 'DeviceThermostat', defaultColor: '#ef4444' },
  orp: { label: 'ORP', unit: 'mV', min: 0, max: 700, optimalMin: 300, optimalMax: 500, meterType: 'gauge', icon: 'Bolt', defaultColor: '#06b6d4' },
  airTemp: { label: 'Air Temp', unit: '°F', min: 50, max: 100, optimalMin: 72, optimalMax: 82, meterType: 'thermometer', icon: 'Thermostat', defaultColor: '#ef4444' },
  humidity: { label: 'Humidity', unit: '%', min: 0, max: 100, optimalMin: 50, optimalMax: 70, meterType: 'humidity', icon: 'WaterDrop', defaultColor: '#3b82f6' },
  co2: { label: 'CO2', unit: 'ppm', min: 0, max: 2000, optimalMin: 800, optimalMax: 1200, meterType: 'co2', icon: 'Air', defaultColor: '#22c55e' },
  vpd: { label: 'VPD', unit: 'kPa', min: 0, max: 3, optimalMin: 0.8, optimalMax: 1.2, meterType: 'gauge', icon: 'Compress', defaultColor: '#ec4899' },
  ppfd: { label: 'PPFD (PAR)', unit: 'µmol/m²/s', min: 0, max: 1500, optimalMin: 600, optimalMax: 1000, meterType: 'light', icon: 'LightMode', defaultColor: '#f59e0b' },
  dli: { label: 'DLI', unit: 'mol/m²/day', min: 0, max: 65, optimalMin: 30, optimalMax: 50, meterType: 'light', icon: 'WbSunny', defaultColor: '#f97316' },
};

// Shared mock sensor values - used by both overview and sensor modules
// In production, this will come from Firestore
export const MOCK_SENSOR_VALUES = {
  ph: 6.2,
  ec: 1.8,
  tds: 890,
  waterTemp: 68.5,
  orp: 320,
  airTemp: 78.2,
  humidity: 62,
  co2: 1150,
  vpd: 1.0,
  ppfd: 850,
  dli: 38.5,
};

// Mock RGB data for PAR sensor
export const MOCK_RGB_DATA = {
  r: 179.720,  // Red: 600-700nm
  g: 120.965,  // Green: 500-600nm
  b: 44.930,   // Blue: 400-500nm
};

// Mock trend data
export const MOCK_TRENDS = {
  ph: { value: 0.1, direction: 'up' },
  ec: { value: 0.05, direction: 'neutral' },
  tds: { value: 15, direction: 'up' },
  waterTemp: { value: 0.5, direction: 'down' },
  orp: { value: 10, direction: 'up' },
  airTemp: { value: 0.8, direction: 'up' },
  humidity: { value: 2, direction: 'down' },
  co2: { value: 25, direction: 'up' },
  vpd: { value: 0.05, direction: 'neutral' },
  ppfd: { value: 25, direction: 'up' },
  dli: { value: 0.5, direction: 'neutral' },
};

// Calculate status color with gradient from green -> yellow -> orange -> red
// based on how far outside optimal range the value is
export function getStatusColorGradient(value, min, max, optimalMin, optimalMax) {
  // Within optimal range - green
  if (value >= optimalMin && value <= optimalMax) {
    return { color: '#22c55e', status: 'optimal' };
  }

  const range = max - min;

  // Calculate how far outside optimal range (as percentage of total range)
  let distanceFromOptimal;
  if (value < optimalMin) {
    distanceFromOptimal = (optimalMin - value) / range;
  } else {
    distanceFromOptimal = (value - optimalMax) / range;
  }

  // Map distance to color gradient
  // 0-10% outside: yellow (#eab308)
  // 10-20% outside: orange (#f97316)
  // 20%+ outside: red (#ef4444)
  if (distanceFromOptimal <= 0.10) {
    return { color: '#eab308', status: 'warning' };  // yellow
  } else if (distanceFromOptimal <= 0.20) {
    return { color: '#f97316', status: 'caution' };  // orange
  }
  return { color: '#ef4444', status: 'critical' };  // red
}

// Get sensor config with optimal range from active grow preset (if available)
// This function should be called with the result of useGrow().getOptimalRange(sensorType)
export function getSensorConfigWithPreset(sensorType, presetOptimal) {
  const baseConfig = SENSOR_TYPES[sensorType];
  if (!baseConfig) return null;

  // If we have preset optimal values, merge them with base config
  if (presetOptimal) {
    return {
      ...baseConfig,
      optimalMin: presetOptimal.optimalMin,
      optimalMax: presetOptimal.optimalMax,
    };
  }

  return baseConfig;
}

// Mock Firestore paths for sensor data
export const FIRESTORE_PATHS = [
  { value: 'sensors/reservoir/ph', label: 'Reservoir → pH' },
  { value: 'sensors/reservoir/ec', label: 'Reservoir → EC' },
  { value: 'sensors/reservoir/tds', label: 'Reservoir → TDS' },
  { value: 'sensors/reservoir/temp', label: 'Reservoir → Temperature' },
  { value: 'sensors/reservoir/orp', label: 'Reservoir → ORP' },
  { value: 'sensors/atmosphere/temp', label: 'Atmosphere → Temperature' },
  { value: 'sensors/atmosphere/humidity', label: 'Atmosphere → Humidity' },
  { value: 'sensors/atmosphere/co2', label: 'Atmosphere → CO2' },
  { value: 'sensors/atmosphere/vpd', label: 'Atmosphere → VPD' },
  { value: 'sensors/lighting/ppfd', label: 'Lighting → PPFD' },
  { value: 'sensors/lighting/dli', label: 'Lighting → DLI' },
  { value: 'sensors/custom/sensor1', label: 'Custom → Sensor 1' },
  { value: 'sensors/custom/sensor2', label: 'Custom → Sensor 2' },
];

// Default sections configuration
const DEFAULT_SECTIONS = [
  {
    id: 'reservoir',
    title: 'Water Reservoir',
    sensors: [
      { id: 'ph-1', type: 'ph', name: 'pH Level', size: 'small', dataPath: 'sensors/reservoir/ph', color: '#3b82f6' },
      { id: 'ec-1', type: 'ec', name: 'EC', size: 'small', dataPath: 'sensors/reservoir/ec', color: '#f59e0b' },
      { id: 'tds-1', type: 'tds', name: 'TDS', size: 'small', dataPath: 'sensors/reservoir/tds', color: '#8b5cf6' },
      { id: 'waterTemp-1', type: 'waterTemp', name: 'Water Temp', size: 'small', dataPath: 'sensors/reservoir/temp', color: '#ef4444' },
      { id: 'orp-1', type: 'orp', name: 'ORP', size: 'small', dataPath: 'sensors/reservoir/orp', color: '#06b6d4' },
    ],
  },
  {
    id: 'atmosphere',
    title: 'Atmosphere',
    sensors: [
      { id: 'airTemp-1', type: 'airTemp', name: 'Air Temp', size: 'small', dataPath: 'sensors/atmosphere/temp', color: '#ef4444' },
      { id: 'humidity-1', type: 'humidity', name: 'Humidity', size: 'small', dataPath: 'sensors/atmosphere/humidity', color: '#3b82f6' },
      { id: 'co2-1', type: 'co2', name: 'CO2', size: 'small', dataPath: 'sensors/atmosphere/co2', color: '#22c55e' },
      { id: 'vpd-1', type: 'vpd', name: 'VPD', size: 'small', dataPath: 'sensors/atmosphere/vpd', color: '#ec4899' },
    ],
  },
  {
    id: 'lighting',
    title: 'Lighting',
    sensors: [
      { id: 'ppfd-1', type: 'ppfd', name: 'PPFD (PAR)', size: 'big', dataPath: 'sensors/lighting/ppfd', color: '#f59e0b' },
      { id: 'dli-1', type: 'dli', name: 'DLI', size: 'big', dataPath: 'sensors/lighting/dli', color: '#f97316' },
    ],
  },
];

const SensorConfigContext = React.createContext(null);

export function SensorConfigProvider({ children }) {
  const [sections, setSections] = React.useState(DEFAULT_SECTIONS);
  const [editingSection, setEditingSection] = React.useState(null);
  const [editingSensor, setEditingSensor] = React.useState(null);

  // Generate unique ID
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Section management
  const addSection = React.useCallback((title = 'New Section') => {
    setSections(prev => [...prev, {
      id: generateId(),
      title,
      sensors: [],
    }]);
  }, []);

  const updateSection = React.useCallback((sectionId, updates) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId ? { ...section, ...updates } : section
    ));
  }, []);

  const deleteSection = React.useCallback((sectionId) => {
    setSections(prev => prev.filter(section => section.id !== sectionId));
  }, []);

  const moveSection = React.useCallback((sectionId, direction) => {
    setSections(prev => {
      const index = prev.findIndex(s => s.id === sectionId);
      if (index === -1) return prev;
      if (direction === 'up' && index === 0) return prev;
      if (direction === 'down' && index === prev.length - 1) return prev;

      const newSections = [...prev];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      [newSections[index], newSections[swapIndex]] = [newSections[swapIndex], newSections[index]];
      return newSections;
    });
  }, []);

  // Sensor management
  const addSensor = React.useCallback((sectionId, sensorType) => {
    const typeConfig = SENSOR_TYPES[sensorType];
    if (!typeConfig) return;

    const newSensor = {
      id: generateId(),
      type: sensorType,
      name: typeConfig.label,
      size: 'small',
      dataPath: '',
      color: typeConfig.defaultColor || '#22c55e',
    };

    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { ...section, sensors: [...section.sensors, newSensor] }
        : section
    ));
  }, []);

  const updateSensor = React.useCallback((sectionId, sensorId, updates) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? {
            ...section,
            sensors: section.sensors.map(sensor =>
              sensor.id === sensorId ? { ...sensor, ...updates } : sensor
            ),
          }
        : section
    ));
  }, []);

  const deleteSensor = React.useCallback((sectionId, sensorId) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { ...section, sensors: section.sensors.filter(s => s.id !== sensorId) }
        : section
    ));
  }, []);

  const moveSensor = React.useCallback((sectionId, sensorId, direction) => {
    setSections(prev => prev.map(section => {
      if (section.id !== sectionId) return section;

      const index = section.sensors.findIndex(s => s.id === sensorId);
      if (index === -1) return section;
      if (direction === 'left' && index === 0) return section;
      if (direction === 'right' && index === section.sensors.length - 1) return section;

      const newSensors = [...section.sensors];
      const swapIndex = direction === 'left' ? index - 1 : index + 1;
      [newSensors[index], newSensors[swapIndex]] = [newSensors[swapIndex], newSensors[index]];
      return { ...section, sensors: newSensors };
    }));
  }, []);

  // Open/close editing modals
  const openSectionEditor = React.useCallback((sectionId) => {
    setEditingSection(sectionId);
  }, []);

  const closeSectionEditor = React.useCallback(() => {
    setEditingSection(null);
  }, []);

  const openSensorEditor = React.useCallback((sectionId, sensorId) => {
    setEditingSensor({ sectionId, sensorId });
  }, []);

  const closeSensorEditor = React.useCallback(() => {
    setEditingSensor(null);
  }, []);

  const value = React.useMemo(() => ({
    sections,
    editingSection,
    editingSensor,
    // Section methods
    addSection,
    updateSection,
    deleteSection,
    moveSection,
    openSectionEditor,
    closeSectionEditor,
    // Sensor methods
    addSensor,
    updateSensor,
    deleteSensor,
    moveSensor,
    openSensorEditor,
    closeSensorEditor,
  }), [
    sections, editingSection, editingSensor,
    addSection, updateSection, deleteSection, moveSection, openSectionEditor, closeSectionEditor,
    addSensor, updateSensor, deleteSensor, moveSensor, openSensorEditor, closeSensorEditor,
  ]);

  return (
    <SensorConfigContext.Provider value={value}>
      {children}
    </SensorConfigContext.Provider>
  );
}

SensorConfigProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useSensorConfig() {
  const context = React.useContext(SensorConfigContext);
  if (!context) {
    throw new Error('useSensorConfig must be used within a SensorConfigProvider');
  }
  return context;
}

export default SensorConfigContext;
